import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink, useParams } from 'react-router-dom';
import MaterialTable from '@material-table/core';
import dayjs from 'dayjs';

import { Box, Button, CircularProgress } from '@mui/material';

import { LinkText } from 'common/components';
import { DeleteOutline as DeleteOutlineIcon, Edit as EditIcon } from '@mui/icons-material';

import { CardWrapper } from 'globalStyles';
import { isEven } from 'utils/helpers';
import { colors } from 'themes';

const formatOfficials = rowData =>
  rowData.targets?.map(target => target.officialName).join(', ') || '';

const formatContacts = rowData =>
  rowData.contacts
    ?.map(contact => `${contact.firstName || ''} ${contact.lastName || ''}`.trim())
    .join(', ') || '';

const MeetingsTable = ({ handleRemoveMeeting, isLoading, meetings }) => {
  const { organizationUuid } = useParams();

  const renderLoader = () => (
    <Box position="relative" display="inline-flex">
      <CircularProgress size={56} />
    </Box>
  );

  const columns = useMemo(
    () => [
      {
        cellStyle: {
          fontWeight: '500',
        },
        field: 'title',
        render: rowData => (
          <LinkText color="inherit" to={`/${organizationUuid}/meetings/${rowData.uuid}`}>
            {rowData.title}
          </LinkText>
        ),
        title: 'Title',
        width: '30%',
      },
      {
        field: 'occurredOn',
        render: rowData => (rowData.occurredOn ? dayjs(rowData.occurredOn).format('M/D/YYYY') : ''),
        title: 'Date',
      },
      {
        customFilterAndSearch: (term, rowData) =>
          formatOfficials(rowData).toLowerCase().includes(term.toLowerCase()),
        field: 'targets',
        render: formatOfficials,
        title: 'Officials',
      },
      {
        customFilterAndSearch: (term, rowData) =>
          formatContacts(rowData).toLowerCase().includes(term.toLowerCase()),
        field: 'contacts',
        render: formatContacts,
        title: 'Contacts',
      },
      {
        align: 'right',
        field: '',
        render: rowData => (
          <>
            <Button
              component={RouterLink}
              startIcon={<EditIcon />}
              to={`/${organizationUuid}/meetings/${rowData.uuid}/edit`}
            >
              Edit
            </Button>
            <Button
              color="error"
              onClick={() => handleRemoveMeeting(rowData.uuid)}
              startIcon={<DeleteOutlineIcon />}
            >
              Delete
            </Button>
          </>
        ),
        title: '',
      },
    ],
    [organizationUuid]
  );

  return (
    <CardWrapper>
      <MaterialTable
        columns={columns}
        data={meetings}
        localization={{
          body: {
            emptyDataSourceMessage: isLoading ? renderLoader() : 'No meetings logged yet',
          },
          toolbar: {
            searchPlaceholder: 'Search',
          },
        }}
        options={{
          actionsColumnIndex: -1,
          headerStyle: {
            color: colors.osloGray,
          },
          maxColumnSort: 0,
          pageSize: 20,
          pageSizeOptions: [5, 20, 50, 100],
          rowStyle: (_, rowNumber) => ({
            backgroundColor: isEven(rowNumber) ? colors.alabaster : colors.white,
            fontSize: '14px',
          }),
          searchDebounceDelay: 400,
          searchFieldAlignment: 'left',
          showFirstLastPageButtons: false,
          showTitle: false,
        }}
        style={{
          boxShadow: 'none',
        }}
      />
    </CardWrapper>
  );
};

MeetingsTable.propTypes = {
  handleRemoveMeeting: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  meetings: PropTypes.arrayOf(
    PropTypes.shape({
      contacts: PropTypes.arrayOf(
        PropTypes.shape({
          firstName: PropTypes.string,
          lastName: PropTypes.string,
        })
      ),
      occurredOn: PropTypes.string,
      targets: PropTypes.arrayOf(
        PropTypes.shape({
          officialName: PropTypes.string,
        })
      ),
      title: PropTypes.string,
      uuid: PropTypes.string,
    })
  ),
};

MeetingsTable.defaultProps = {
  isLoading: false,
  meetings: [],
};

export default MeetingsTable;
